// How records read on screen, in Souvenir's voice: short, plain, facts separated by " · ".
// Plain functions, so the server-side tests can use them too.
import { iconOf, type Kind, kindOf } from "../../src/categories.ts";
import { roundMoney } from "../../src/money.ts";
import { TIERS, TIERS_LISTED, type Tier } from "../../src/tiers.ts";
import type { Why } from "../../src/tobook.ts";
import { isSuggestion, ownerNow } from "../../src/waits.ts";
import type { Item, Person, When } from "../../src/web/types.ts";
import { zoneCity } from "../../src/zoned.ts";
import type { IconName } from "../souvenir/index.js";
import type { TripmoIcon } from "./icons.ts";

const MONTH = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const MONTH_YEAR = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const WEEKDAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

const isDate = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
const utc = (d: string) => new Date(`${d}T00:00:00Z`);
// "day 3" reads "Day 3"; a date reads "Wed 2 Dec".
const day = (d: string) => (isDate(d) ? WEEKDAY.format(utc(d)) : d.replace(/^day/, "Day"));
const time = (t: string) => t.slice(11, 16);

// What was paid, to the cent the currency has, never ".00": "SGD 609.93", "¥79,200", "SGD 610"; and totals and
// estimates, rounded: "SGD 1,673" (src/money.ts, which Tripmo's reminders say money with too). Neither ever throws,
// whatever the currency (DESIGN.md, the log row "Money without an estimate").
export { paidMoney, roundMoney } from "../../src/money.ts";
// "Asia/Singapore" is "Singapore" (src/zoned.ts).
export { zoneCity } from "../../src/zoned.ts";

// A price as an option or a card says it: "About SGD 600 for the group", "¥4,000 per person".
export function price(cost: NonNullable<Item["cost"]>): string {
  const per = cost.per === "person" ? " per person" : cost.per === "group" ? " for the group" : "";
  return `${cost.estimate ? "About " : ""}${roundMoney(cost.amount, cost.currency)}${per}`;
}

// A plain date with its weekday: "Thu 19 Nov".
export const weekday = (d: string) => WEEKDAY.format(utc(d));
// A reminder's own local time, as it was set (features/tasks-and-reminders.md): "Wed 18 Nov, 09:00 Singapore time".
export const localWhen = (at: string, tz: string) =>
  `${weekday(at.slice(0, 10))}, ${at.slice(11, 16)} ${zoneCity(tz)} time`;
// A day as said on its own: a date with its weekday ("Wed 2 Dec"), or "Day 3".
export const dayLabel = (d: string) => day(d);

// Today on this device, as a plain date ("2026-10-20"): what's due is counted from the reader's own day.
export function localToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// The trip's dates, as the eyebrow over its title: "28 Nov 2026 – 2 Jan 2027". Null when it has none.
export function tripDates(when: When): string | null {
  if (when.precision === "days" && isDate(when.start) && isDate(when.end)) {
    const a = utc(when.start);
    const b = utc(when.end);
    if (a.getUTCFullYear() === b.getUTCFullYear()) return `${MONTH.format(a)} – ${MONTH_YEAR.format(b)}`;
    return `${MONTH_YEAR.format(a)} – ${MONTH_YEAR.format(b)}`;
  }
  if (when.precision === "day" && isDate(when.day)) return MONTH_YEAR.format(utc(when.day));
  return null;
}

// The same, shorter, for a header where both years don't fit on one line beside the faces (DESIGN.md, "The
// trip's name is its menu"): the year said once, at the end, where a year's end is plain from the months
// ("28 Nov – 2 Jan 2027"). Null where there's nothing shorter to say: a trip within one year already says
// its year once, and one ending a year later in a month at or after its first ("28 Nov 2026 – 2 Dec 2027")
// needs both years.
export function tripDatesShort(when: When): string | null {
  if (when.precision !== "days" || !isDate(when.start) || !isDate(when.end)) return null;
  const a = utc(when.start);
  const b = utc(when.end);
  if (b.getUTCFullYear() - a.getUTCFullYear() === 1 && b.getUTCMonth() < a.getUTCMonth())
    return `${MONTH.format(a)} – ${MONTH_YEAR.format(b)}`;
  return null;
}

// The days a group of bookings spans, short: "1 Dec", "1 to 25 Dec", "28 Nov to 2 Jan", "Day 2 to 6".
export function daysSpanned({ first, last }: { first: string; last: string }): string {
  const short = (d: string) => (isDate(d) ? MONTH.format(utc(d)) : d.replace(/^day/, "Day"));
  if (first === last) return short(first);
  if (isDate(first) && isDate(last) && first.slice(0, 7) === last.slice(0, 7))
    return `${utc(first).getUTCDate()} to ${short(last)}`;
  if (!isDate(first) && !isDate(last)) return `${short(first)} to ${last.replace(/^day /, "")}`;
  return `${short(first)} to ${short(last)}`;
}

// When an item happens, at the precision it was given: "Day 6 · morning", "Wed 2 Dec · 10:00".
export function itemWhen(when: When | undefined): string | null {
  if (!when) return null;
  switch (when.precision) {
    case "none":
      return null;
    case "day":
      return day(when.day);
    case "days":
      return `${day(when.start)} – ${day(when.end)}`;
    case "part":
      return when.day ? `${day(when.day)} · ${when.part}` : `Any ${when.part}`;
    case "window":
    case "exact": {
      // Ending another day (a stay from check-in to check-out, a night train): both days with their times.
      const end = when.end;
      if (end && end.slice(0, 10) !== when.start.slice(0, 10))
        return `${day(when.start.slice(0, 10))} · ${time(when.start)} – ${day(end.slice(0, 10))} · ${time(end)}`;
      return `${day(when.start.slice(0, 10))} · ${time(when.start)}${end ? `–${time(end)}` : ""}`;
    }
  }
}

export const STATUS: Record<string, { label: string; icon: IconName; tone?: "sun" | "sea" | "coral" | "moss" }> = {
  idea: { label: "Idea", icon: "note" },
  planned: { label: "Planned", icon: "check", tone: "moss" },
  booked: { label: "Booked", icon: "lock", tone: "sun" },
  cancelled: { label: "Cancelled", icon: "close" },
  done: { label: "Done", icon: "check", tone: "moss" },
  skipped: { label: "Skipped", icon: "close" },
  missed: { label: "Missed", icon: "close" },
  dropped: { label: "Dropped", icon: "close" },
  todo: { label: "To do", icon: "note" },
  option: { label: "Option", icon: "note" },
  not_chosen: { label: "Not chosen", icon: "close" },
};

// What a thing is, broadly, to filter the map by: a stay, a move, else from its category.
export const KINDS = [
  { key: "stays", label: "Stays", icon: "bed" },
  { key: "around", label: "Getting around", icon: "route" },
  { key: "food", label: "Food and drink", icon: "meal" },
  { key: "sights", label: "Sights", icon: "torii" },
  { key: "nature", label: "Nature", icon: "tree" },
  { key: "shopping", label: "Shopping", icon: "bag" },
  { key: "other", label: "Other", icon: "pin" },
] as const satisfies readonly { key: Kind; label: string; icon: IconName }[];

export type { Kind };
export { kindOf };

// Every icon a category, a mode or a kind of thing is matched to (src/categories.ts): one Souvenir
// draws, else Tripmo's own in icons.ts. Typed as one, so a name nothing draws fails to compile.
export type ItemIconName = IconName | TripmoIcon;

// Each kind's colour, behind its icon wherever the icon shows: Tripmo's own tokens in app.css
// (`--kind-<key>` and `--on-kind-<key>`, light and dark), which this class puts on `--kind` and
// `--on-kind` for the element and what's inside it.
export const kindClass = (kind: Kind) => `tm-kind-${kind}`;

export function itemIcon(item: Pick<Item, "type" | "category"> & { from?: unknown; title?: string }): ItemIconName {
  return iconOf(item);
}

export function itemKind(item: Item): string {
  const kind =
    item.type === "stay"
      ? "Where we sleep"
      : item.type === "goal"
        ? "Goal"
        : item.type === "choice"
          ? "To decide"
          : item.type === "task"
            ? "To-do"
            : sentence(item.category ?? (item.type === "move" ? "Journey" : item.type === "info" ? "Info" : "Place"));
  const area = item.where?.name && item.where.name !== item.title ? item.where.name : null;
  return area ? `${kind} · ${area}` : kind;
}

// A date as people and AIs write one (DESIGN.md, Bookings: "never breaking inside a date"): a day and its month
// either way round ("25 Dec", "Dec 25", "25th December"), with its weekday ("Sat 28 Nov", "Saturday, 28 Nov") and
// year ("28 Nov 2026"), and a run or range of days before or after their month ("5, 7, 10 Dec", "5-7 Dec",
// "Dec 5 & 7"). A month's name must be there, so a count or a code never reads as one ("Pass ×4", "T2",
// "5 people", "car 4"), and a day never starts inside a word, a time or a code ("T2 25 Dec" holds "25 Dec").
const MONTH_NAME =
  "Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?";
const WEEKDAY_NAME =
  "Mon(?:day)?|Tue(?:s(?:day)?)?|Wed(?:nesday)?|Thu(?:rs?(?:day)?)?|Fri(?:day)?|Sat(?:urday)?|Sun(?:day)?";
const DAY = "\\d{1,2}(?:st|nd|rd|th)?";
const DAYS = `${DAY}(?:(?:\\s*[,&–-]\\s*|\\s+(?:and|to)\\s+)${DAY})*`;
const DATE = `(?<![\\w×#.:/-])(?:(?:${WEEKDAY_NAME}),?\\s+)?(?:${DAYS}\\s+(?:${MONTH_NAME})|(?:${MONTH_NAME})\\s+${DAYS})\\b(?:,?\\s+\\d{4}\\b)?`;
// Longer than this, a date may not fit a phone's row whole ("Wednesday 25th December 2026"): it wraps as words do.
const LONGEST_HELD_DATE = 22;

// A title as its parts, each date in it whole (`date`), so a row's title breaks between the words around a
// date and never inside one: "Reserve shinkansen/Thunderbird seats for 5, 7, 10 Dec" never ends a line "for
// 5, 7," (the lead's call, 2026-10-23, from #526). Nothing else in a title is held.
export function titleParts(text: string): { text: string; date: boolean }[] {
  const parts: { text: string; date: boolean }[] = [];
  let at = 0;
  for (const m of text.matchAll(new RegExp(DATE, "g"))) {
    if (m[0].length > LONGEST_HELD_DATE) continue;
    if (m.index > at) parts.push({ text: text.slice(at, m.index), date: false });
    parts.push({ text: m[0], date: true });
    at = m.index + m[0].length;
  }
  if (at < text.length) parts.push({ text: text.slice(at), date: false });
  return parts;
}

// What a line never breaks inside though it has spaces: a date (above), a day of the trip ("Day 3"), an amount
// after its currency's code ("SGD 1,074"; Intl already holds its own with a no-break space).
const WHOLE = new RegExp(`${DATE}|\\bDay \\d+\\b|\\b[A-Z]{3} \\d[\\d,.]*`, "g");
// A mark that reads with the word before it, so it never starts a line: "3906-5167-1399 /", "Sat 28 Nov ·".
const MARK = /^[·–—/&+=,;:]$/;
// Stands for a space inside what's held, while the rest is split at its spaces.
const HELD_SPACE = "\uE000";

// How long a fact may be and still never break inside (ui.tsx, factLine): a booking's reference, "in 5 days".
export const SHORT_FACT = 28;

// Some words as the units a line may break between: each word whole (a booking's reference, "3906-5167-1399",
// never "3906-" over "5167-1399"), each date and amount whole, a mark with the word before it.
export function heldWords(text: string): string[] {
  const marked = text.replace(WHOLE, (m) => m.replaceAll(" ", HELD_SPACE));
  const out: string[] = [];
  for (const word of marked.split(/ +/)) {
    if (!word) continue;
    const unit = word.replaceAll(HELD_SPACE, " ");
    const before = out.length - 1;
    if (MARK.test(unit) && before >= 0) out[before] = `${out[before]} ${unit}`;
    else out.push(unit);
  }
  return out;
}

// A line starts with a capital, whichever of its parts comes first.
export const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Each tier's name, as a button, a heading or a tally says it (src/tiers.ts): one word wherever it shows, so
// no row swaps in a shorter one (the founder, 2026-10-23: "No", not "Not interested").
export const TIER_LABEL: Readonly<Record<Tier, string>> = { no: "No", maybe: "Maybe", yes: "Yes" };

export const listNames = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// "You and Mei want it · Ben not yet": who wants an item, from the reader's side.
export function whoWants(item: Item, members: Person[], you: string): string {
  return whoWantsParts(item, members, you).join(" · ");
}

// The same, as its facts, for a line that may wrap (factLine).
export function whoWantsParts(item: Item, members: Person[], you: string): string[] {
  const name = (id: string) => (id === you ? "You" : (members.find((m) => m.id === id)?.name ?? "Someone"));
  // Only people still in the trip: someone who left keeps their history, not a say.
  const inTrip = new Set(members.map((m) => m.id));
  const wanting = item.interest.filter((i) => i.tier && i.tier !== "no" && inTrip.has(i.person)).map((i) => i.person);
  const rated = new Set(item.interest.filter((i) => i.tier).map((i) => i.person));
  const notYet = members.filter((m) => !rated.has(m.id)).map((m) => (m.id === you ? "you" : m.name));
  const ordered = [...wanting].sort((a, b) => (a === you ? -1 : b === you ? 1 : 0));
  const head = ordered.length
    ? `${listNames(ordered.map(name))} ${ordered.length === 1 && ordered[0] !== you ? "wants" : "want"} it`
    : "Nobody's said they want it yet";
  return notYet.length && ordered.length ? [head, `${listNames(notYet)} not yet`] : [head];
}

export function km(m: number): string {
  return m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`;
}

export function ago(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

// When something was last used, by the calendar here: "today", "yesterday", "3 days ago", "on 12 Oct".
export function lastUsed(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
  const days = day(now) - day(at);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const sameYear = at.getFullYear() === now.getFullYear();
  return `on ${at.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) })}`;
}

// A moment's day, in the reader's own calendar, with its weekday: "Mon 16 Nov" (a deleted trip's last
// day, the day it was deleted). Another year's says so: "Thu 7 Jan 2027". It never breaks across lines.
const LOCAL_DAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" });
export function lastDay(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const part = (type: Intl.DateTimeFormatPartTypes) => LOCAL_DAY.formatToParts(at).find((p) => p.type === type)?.value;
  const year = at.getFullYear() === now.getFullYear() ? "" : `\u00a0${at.getFullYear()}`;
  return `${part("weekday")}\u00a0${part("day")}\u00a0${part("month")}${year}`;
}

// "Yes from you and Ben · Maybe from Mei": who wants an item, by tier, Yes first, from the reader's side.
export function wantsLine(item: Item, members: Person[], you: string): string {
  const name = (id: string) => (id === you ? "you" : (members.find((m) => m.id === id)?.name ?? null));
  const by = (tier: string) =>
    item.interest
      .filter((x) => x.tier === tier)
      .map((x) => name(x.person))
      .filter((n): n is string => n !== null)
      .sort((a, b) => (a === "you" ? -1 : b === "you" ? 1 : 0));
  const parts = [
    by("yes").length ? `Yes from ${listNames(by("yes"))}` : null,
    by("maybe").length ? `Maybe from ${listNames(by("maybe"))}` : null,
  ];
  return parts.filter(Boolean).join(" · ") || "Nobody's said they want it yet";
}

// A person named by member id (mem_…) or person id, from the reader's side ("You").
export function personName(ref: string | undefined, members: Person[], you: string): string | null {
  if (!ref) return null;
  const m = members.find((p) => p.member === ref || p.id === ref);
  return m ? (m.id === you ? "You" : m.name) : null;
}

// Who's booking something now (a person id): its booker while they're in the trip, else the owner of the
// to-do that books it while they are; nobody when neither. One who has left the trip counts as nobody named,
// as a to-do's departed owner does (src/waits.ts, `ownerNow`; features/tasks-and-reminders.md).
export function whoBooks(
  item: Pick<Item, "booking">,
  task: Pick<Item, "owner"> | null | undefined,
  members: ReadonlySet<string>,
): string | undefined {
  return ownerNow({ owner: item.booking?.booker }, members) ?? ownerNow({ owner: task?.owner }, members);
}

// Who's booking it, as a line's fact: "Mei books", "You book", "nobody's booking it yet".
export function booksLine(person: string | undefined, members: Person[], you: string): string {
  const name = personName(person, members, you);
  return name ? `${name} ${name === "You" ? "book" : "books"}` : "nobody's booking it yet";
}

// Who books something to book, as its row says it: only when that's someone other than its to-do's owner, whose
// its to-do's own facts say right after the state ("To book · nobody's yet · by 25 Oct · … · Mei books"; the
// lead's call, 2026-10-23); with no to-do, always ("To book · You said yes · nobody's booking it yet").
export function bookerFact(
  item: Pick<Item, "booking">,
  task: Pick<Item, "owner"> | null,
  inTrip: ReadonlySet<string>,
  members: Person[],
  you: string,
): string | null {
  const books = whoBooks(item, task, inTrip);
  if (task && books === ownerNow({ owner: task.owner }, inTrip)) return null;
  return booksLine(books, members, you);
}

// An AI's suggestion: "Suggested by Claude".
export function originLabel(item: Pick<Item, "origin">): string | null {
  return item.origin?.kind === "ai" ? `Suggested by ${item.origin.by ?? "an AI"}` : null;
}

// The friends who recommend it, by name, in the order they did.
export function recommenders(item: Pick<Item, "recommended_by">): string[] {
  return [...new Set((item.recommended_by ?? []).map((r) => r.name))];
}

// "Jon's pick", "Jon and Aiko's pick", "Jon, Aiko and 2 more": what a row or a pin says about it.
export function picksLabel(item: Pick<Item, "recommended_by">): string | null {
  const names = recommenders(item);
  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]}'s pick`;
  if (names.length === 2) return `${names[0]} and ${names[1]}'s pick`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

// An AI's suggestion that nobody in the trip has said they want yet. (A friend's pick isn't waiting:
// it carries the friend's weight already.)
export function waitingSuggestion(item: Pick<Item, "origin" | "interest">, members: Person[]): boolean {
  if (!item.origin) return false;
  const ids = new Set(members.map((m) => m.id));
  return !item.interest.some((x) => ids.has(x.person) && x.tier && x.tier !== "no");
}

// The three as buttons, left to right as everywhere they're offered: No, Maybe, Yes.
export const RATINGS = TIERS.map((key) => ({ key, label: TIER_LABEL[key] }));
export const ratingLabel = (tier: string) => TIER_LABEL[tier as Tier] ?? tier;

// A person's rating said short, as a reason or beside their face: "Mei said yes", "You and Ben said maybe",
// "Ben said no" (the founder, 2026-10-23: "Yes" all the way, so "Mei must" became "Mei said yes").
export const saidWords = (tier: string) => `said ${ratingLabel(tier).toLowerCase()}`;
export const saidShort = (who: string, tier: string) => `${who} ${saidWords(tier)}`;

// Where tiers are listed (everyone's rating, a tally), Yes first: Yes, Maybe, No.
export const byTierOrder = (a: string, b: string) => TIERS_LISTED.indexOf(a as Tier) - TIERS_LISTED.indexOf(b as Tier);

// My own tier on an item, or null while I haven't rated it.
export function myTier(item: Pick<Item, "interest">, you: string): string | null {
  return item.interest.find((x) => x.person === you)?.tier ?? null;
}

// An AI's suggestion still being considered (src/waits.ts, shared with the server): the map's quieter pin and
// get_trip's `waiting.suggestions_unrated_by_you`. What Ideas' Suggestions lists is wider (suggestions.ts).
export { isSuggestion };

// Why something is to book (src/tobook.ts), from the reader's side: "Mei said yes", "You and Ben said yes",
// "Ben's going", "on Tue 8 Dec · 10:00", "chosen", "planned". A to-do for it says nothing more: its whose and
// by when, said as a to-do's row says them ("nobody's yet · by 25 Oct"), are the reason (DESIGN.md, Bookings;
// until 2026-10-23 it said "to-do, nobody's yet, by 25 Oct").
export function toBookWhy(why: Why, item: Item, members: Person[], you: string): string | null {
  const people = (ids: string[]) => {
    const names = [...ids]
      .sort((a, b) => (a === you ? -1 : b === you ? 1 : 0))
      .map((id) => (id === you ? "You" : (members.find((m) => m.id === id)?.name ?? "Someone")));
    return { names: listNames(names), one: names.length === 1, you: ids.includes(you) };
  };
  switch (why.kind) {
    case "chosen":
      return "chosen";
    case "planned":
      return "planned";
    case "day":
      return `on ${itemWhen(item.when)}`;
    case "todo":
      return null;
    case "yes":
      return saidShort(people(why.people).names, "yes");
    case "going": {
      const p = people(why.people);
      return p.one ? (p.you ? "You're going" : `${p.names}'s going`) : `${p.names} are going`;
    }
  }
}
